<template>
  <div class="beneficiary-info-geo">
    <div class="col-md-4">
      <div class="form-group w-100">
        <div class="group-title">Country <b class="text-red">*</b></div>
        <dropdown
          v-loading="loadingCountries"
          v-validate="validationRules.country"
          data-vv-as="Country"
          search-placeholder="Search"
          drop-value="Select..."
          :name="validation.COUNTRY"
          :value="country"
          :show-search="true"
          :options="filteredCountries"
          :class="{'has-error': errors.has(validation.COUNTRY)}"
          @search="handleChangeSearch($event, 'Country')"
          @input="handleChange($event, 'Country')"
        >
          <template #header="{ value }">
            <span>{{ get(value, 'country_name') || value }}</span>
          </template>
          <template #item="{ option }">
            <span>{{ get(option, 'country_name') }}</span>
          </template>
        </dropdown>
        <span v-if="errors.has(validation.COUNTRY)" class="help-text">
          {{ errors.first(validation.COUNTRY) }}
        </span>
      </div>
    </div>
    <div class="col-md-4">
      <div class="form-group w-100">
        <div class="group-title">State <b class="text-red">*</b></div>
        <div :class="{disabled: isStateDisabled}">
          <dropdown
            v-loading="loadingStates"
            v-validate="validationRules.state(!isNotStates)"
            data-vv-as="State"
            search-placeholder="Search"
            :drop-value="selectStateTooltip"
            :name="validation.STATE"
            :value="state"
            :show-search="true"
            :options="filteredStates"
            :class="{'has-error': errors.has(validation.STATE) && !isStateDisabled}"
            @search="handleChangeSearch($event, 'State')"
            @input="handleChange($event, 'State')"
          >
            <template #header="{ value }">
              <span>{{ get(value, 'state') || value }}</span>
            </template>
            <template #item="{ option }">
              <span>{{ get(option, 'state') }}</span>
            </template>
          </dropdown>
          <span v-if="errors.has(validation.STATE) && !isStateDisabled" class="help-text">
            {{ errors.first(validation.STATE) }}
          </span>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="form-group w-100">
        <div class="group-title">City <b class="text-red">*</b></div>
        <div :class="{disabled: isCityDisabled}">
          <dropdown
            v-loading="loadingCities"
            v-validate="validationRules.city"
            data-vv-as="City"
            search-placeholder="Search"
            :drop-value="selectCityTooltip"
            :name="validation.CITY"
            :value="city"
            :show-search="true"
            :options="filteredCities"
            :class="{'has-error': errors.has(validation.CITY) && !isCityDisabled}"
            @search="handleChangeSearch($event, 'City')"
            @input="handleChange($event, 'City')"
          >
            <template #header="{ value }">
              <span>{{ get(value, 'city_name') || value }}</span>
            </template>
            <template #item="{ option }">
              <span>{{ get(option, 'city_name') }}</span>
            </template>
          </dropdown>
          <span v-if="errors.has(validation.CITY) && !isCityDisabled" class="help-text">
            {{ errors.first(validation.CITY) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {get} from 'lodash';
import {GeoService} from "@/services/geoService";
import {Validation, validationRules} from "@/services/billingBeneficiaryService";

const service = new GeoService();
/** @type {Country[]}*/
let loadedCountries = [];
/** @type {Object<string, State[]>}*/
const loadedStates = {};
/** @type {Object<string, City[]>}*/
const loadedCities = {};

/** @typedef {'Country'|'State'|'City'} Type */
export default {
  name: 'BeneficiaryInfoGeo',

  props: {
    country: {
      type: Object,
      default: () => {}
    },
    state: {
      type: Object,
      default: () => {}
    },
    city: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      /** @type {Country[]} */
      countries: [],
      searchCountry: '',
      loadingCountries: false,

      /** @type {State[]} */
      states: [],
      searchState: '',
      loadingStates: false,
      isNotStates: false,

      /** @type {City[]} */
      cities: [],
      searchCity: '',
      loadingCities: false,
    }
  },

  computed: {
    filteredCountries() {
      if (!this.searchCountry) {
        return this.countries;
      }

      return this.countries.filter(element => {
        return element.country_name.toLowerCase().includes(this.searchCountry.toLowerCase())
      });
    },

    filteredStates() {
      if (!this.searchState) {
        return this.states;
      }

      return this.states.filter(element => {
        return element.state.toLowerCase().includes(this.searchState.toLowerCase())
      });
    },

    filteredCities() {
      if (!this.searchCity) {
        return this.cities;
      }

      return this.cities.filter(element => {
        return element.city_name.toLowerCase().includes(this.searchCity.toLowerCase())
      });
    },

    selectStateTooltip() {
      if (!this.country) {
        return 'Select Country first';
      }

      if (!this.state && this.isNotStates) {
        return 'Not states';
      }

      return 'Select...';
    },

    selectCityTooltip() {
      if (!this.country) {
        return 'Select Country first';
      }

      if (!this.state && !this.isNotStates) {
        return 'Select State first';
      }

      return 'Select...';
    },

    isStateDisabled() {
      return !this.country || this.isNotStates;
    },

    isCityDisabled() {
      return !this.country || (!this.state && !this.isNotStates);
    },

    isGeoConsistent() {
      if (!this.country || !this.city)  {
        return false;
      }

      if (this.state && this.state.country_iso3 !== this.country.iso3) {
        return false;
      }

      if (this.state && this.state.state_iso2 !== this.city.state_iso2) {
        return false;
      }

      return this.city.country_iso3 === this.country.iso3;
    },

    validation() {
      return Validation;
    },

    validationRules() {
      return validationRules;
    },

    errors() {
      return this.$validator.errors;
    }
  },

  watch: {
    country: {
      async handler() {
        if (!this.country) {
          return;
        }

        if (!this.isGeoConsistent) {
          this.handleChange(null, 'State');
          this.handleChange(null, 'City');
        }

        this.isNotStates = false;

        await this.setStates(this.country.iso3);
        if (this.states.length) {
          return;
        }

        this.isNotStates = true;
        this.setCities(this.country.iso3);
      },

      immediate: true
    },

    state: {
      handler() {
        if (!this.isGeoConsistent) {
          this.handleChange(null, 'City');
        }

        if (this.state) {
          this.setCities(this.state.country_iso3, this.state.state_iso2);
        }
      },

      immediate: true
    },
  },

  beforeCreate() {
    service.axios = this.$axios;
  },

  mounted() {
    this.setCountries();
  },

  methods: {
    get,

    setCountries() {
      if (loadedCountries.length) {
        this.countries = loadedCountries;
        return;
      }

      this.loadingCountries = true;
      service.getCountries()
        .then(({data}) => {
          this.countries = data
          loadedCountries = data;
        })
        .finally(() => {
          this.loadingCountries = false
        });
    },

    /**
     * @param {string} country_iso3
     *
     * @return {void}
     */
    async setStates(country_iso3) {
      const loaded = this.getLoaded(loadedStates, country_iso3);
      if (loaded) {
        this.states = loaded;
        return;
      }

      this.loadingStates = true;
      await service.getStateByCountryISO(country_iso3)
        .then(({data}) => {
          let result = data;
          if (result.length === 1 && result[0].state_iso2 === '') {
            result = [];
          }

          this.states = result.map(state => {
            if (state.state === '') {
              state.state = 'Not applicable'
            }

            return state;
          });
          loadedStates[country_iso3] = result;
        })
        .finally(() => {
          this.loadingStates = false
        });
    },

    /**
     * @param {string} country_iso3
     * @param {string|null} state_iso2
     *
     * @return { void }
     */
    async setCities(country_iso3, state_iso2 = null) {
      const key = `${country_iso3}_${state_iso2}`;
      const loaded = this.getLoaded(loadedCities, key);
      if(loaded) {
        this.cities = loaded;
        return;
      }

      this.loadingCities = true;
      await service.getCitiesByISO(country_iso3, state_iso2)
        .then(({data}) => {
          this.cities = data;
          loadedCities[key] = data;
        })
        .finally(() => {
          this.loadingCities = false
        });
    },

    /**
     * @template T
     * @param {Object<string, T[]>} loaded
     * @param {string} key
     *
     * @return { T[]|null }
     */
    getLoaded(loaded, key) {
      return Object.prototype.hasOwnProperty.call(loaded, key) ? loaded[key] : null;
    },

    /**
     * @param {string} value
     * @param {Type} type
     *
     * @return { void }
     */
    handleChangeSearch(value, type) {
      this[`search${type}`] = value;
    },

    /**
     * @param {string|null} value
     * @param {Type} type
     *
     * @return { void }
     */
    handleChange(value, type) {
      this.$emit(`update:${type.toLowerCase()}`, value)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/scss/variables';

.beneficiary-info-geo {
  display: contents;

  .form-group {
    max-width: initial;

    :deep(.dropdown) {
      &.has-error .dropdown-trigger {
        border: 1px solid $main-error;
      }

      .loading {
        background-size: 100px;
      }

      .dropdown-item.active {
        pointer-events: none;

        &:focus {
          color: #fff;
        }
      }
    }
  }

  .disabled {
    cursor: not-allowed;

    :deep(.dropdown) {
      pointer-events: none;

      .dropdown-trigger {
        background-color: #f8f8f8;
        box-shadow: none;

        .icon {
          visibility: hidden;
        }
      }
    }
  }

  .help-text {
    padding-left: 20px;
    display: block;
    color: $main-error;
    background: url('../../images/alert-circle.svg') 0 50% no-repeat;
  }
}
</style>
